const BASE_URL = '/api'

/** Preserves the HTTP status and parsed error body so callers can branch on e.g. 404 vs 409. */
export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * The server's own message for a failed call, verbatim.
 *
 * Every route in this app answers a refusal with `{ error }` written to be read by whoever pressed
 * the button — the 409s on the deletion routes explain what to do next, and
 * flattening those into "Something went wrong" throws away the only useful half. Falls back to the
 * transport-level message when the body carried none.
 */
export function serverErrorMessage(failure: unknown): string {
  if (
    failure instanceof ApiError &&
    failure.body &&
    typeof failure.body === 'object' &&
    'error' in failure.body
  ) {
    return String((failure.body as { error: unknown }).error)
  }
  return failure instanceof Error ? failure.message : String(failure)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE_URL + path, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    const message = `API ${init?.method ?? 'GET'} ${path} failed with ${res.status}`
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = undefined
    }
    throw new ApiError(res.status, body, message)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  /** Escape hatch for the few calls that need a method/body combination the helpers don't cover. */
  request,
  get: <T>(path: string): Promise<T> => request<T>(path),
  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
}
